import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { StatutsProvider } from "@/context/StatutsContext";
import { Toaster } from "@/components/ui/sonner";
import { Loader2 } from "lucide-react";

import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import ChangePassword from "@/pages/ChangePassword";
import ResetPassword from "@/pages/ResetPassword";
import Dashboard from "@/pages/Dashboard";
import RendezVous from "@/pages/RendezVous";
import VisiteDetail from "@/pages/VisiteDetail";
import Planification from "@/pages/Planification";
import Carte from "@/pages/Carte";
import Tournee from "@/pages/Tournee";
import MaTournee from "@/pages/MaTournee";
import PlusProche from "@/pages/PlusProche";
import Clients from "@/pages/Clients";
import ClientDetail from "@/pages/ClientDetail";
import Partenaires from "@/pages/Partenaires";
import Utilisateurs from "@/pages/Utilisateurs";
import Statistiques from "@/pages/Statistiques";
import Comptabilite from "@/pages/Comptabilite";
import ParametrageVT from "@/pages/Parametrage";
import MesJetons from "@/pages/MesJetons";
import { PaymentSuccess, PaymentCancel } from "@/pages/PaymentStatus";
import { NovaSolDashboard, NovaSolRendezVous, NovaSolDossiers } from "@/pages/NovaSol";

function Splash() {
  return (
    <div className="flex h-screen items-center justify-center bg-[#F8FAFC]">
      <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
    </div>
  );
}

function Protected({ children }) {
  const { user, ready } = useAuth();
  if (!ready) return <Splash />;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function PublicOnly({ children }) {
  const { user, ready } = useAuth();
  if (!ready) return <Splash />;
  if (user) return <Navigate to="/" replace />;
  return children;
}

function RoleGuard({ roles, children }) {
  const { user } = useAuth();
  if (roles && !roles.includes(user.role)) return <Navigate to="/" replace />;
  return children;
}

function AppRoutes() {
  const R = {
    interne: ["admin", "planification"],
    admin: ["admin"],
    clients: ["admin", "planification", "installateur"],
    partenaire: ["installateur"],
  };
  return (
    <Routes>
      <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route element={<Protected><Layout /></Protected>}>
        <Route index element={<Dashboard />} />
        <Route path="/mot-de-passe" element={<ChangePassword />} />
        <Route path="/rendez-vous" element={<RendezVous />} />
        <Route path="/visites/:id" element={<VisiteDetail />} />
        <Route path="/planification" element={<Planification />} />
        <Route path="/carte" element={<RoleGuard roles={R.interne}><Carte /></RoleGuard>} />
        <Route path="/tournee" element={<RoleGuard roles={R.interne}><Tournee /></RoleGuard>} />
        <Route path="/ma-tournee" element={<RoleGuard roles={["technicien"]}><MaTournee /></RoleGuard>} />
        <Route path="/plus-proche" element={<RoleGuard roles={R.interne}><PlusProche /></RoleGuard>} />
        <Route path="/clients" element={<RoleGuard roles={R.clients}><Clients /></RoleGuard>} />
        <Route path="/clients/:id" element={<RoleGuard roles={R.clients}><ClientDetail /></RoleGuard>} />
        <Route path="/partenaires" element={<RoleGuard roles={R.interne}><Partenaires /></RoleGuard>} />
        <Route path="/utilisateurs" element={<RoleGuard roles={R.interne}><Utilisateurs /></RoleGuard>} />
        <Route path="/statistiques" element={<RoleGuard roles={R.interne}><Statistiques /></RoleGuard>} />
        <Route path="/comptabilite" element={<RoleGuard roles={R.admin}><Comptabilite /></RoleGuard>} />
        <Route path="/parametrage" element={<RoleGuard roles={R.admin}><ParametrageVT /></RoleGuard>} />
        <Route path="/mes-jetons" element={<RoleGuard roles={R.partenaire}><MesJetons /></RoleGuard>} />
        <Route path="/novasol" element={<RoleGuard roles={R.interne}><NovaSolDashboard /></RoleGuard>} />
        <Route path="/novasol/rendez-vous" element={<RoleGuard roles={R.interne}><NovaSolRendezVous /></RoleGuard>} />
        <Route path="/novasol/dossiers" element={<RoleGuard roles={R.interne}><NovaSolDossiers /></RoleGuard>} />
        <Route path="/payment/success" element={<PaymentSuccess />} />
        <Route path="/payment/cancel" element={<PaymentCancel />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <StatutsProvider>
        <BrowserRouter>
          <AppRoutes />
          <Toaster position="top-right" richColors />
        </BrowserRouter>
      </StatutsProvider>
    </AuthProvider>
  );
}
